import { useEffect } from "react";

import { type QueryClient, skipToken, useMutation, useQueryClient } from "@tanstack/react-query";

import { EToySessionState, toysGateAPI } from "@api/toys";
import type { IPlayRequest, IUpdateMotionRequest, TGetToysSessionResponse } from "@api/toys";

import { useToysStore } from "@storage/global/useToysStore";

import { useAuthQuery } from "@lib/queryClient";

import { capitalizeFirstLetter } from "@utils/capitalizeFirstLetter";

import { API_PREFIXES, QUERY_KEYS } from "@configs/api";
import { STALE_INFINITY } from "@configs/constants";
import { EProviderName } from "@configs/toys";

import { useToysGateRequest } from "./useToysGateRequest";

const keepLatestToysSession = (current: TGetToysSessionResponse | undefined, next: TGetToysSessionResponse) =>
	current?.session_id === next.session_id && current.version >= next.version ? current : next;

/**
 * Add for preventing duplicate stop/play requests. Blinking stop/play button UI.
 * A delayed GET must not roll back a newer Play/Stop response (or vice versa)
 * Singleflight pattern
 */
const updateToysSessionCache = (
	queryClient: QueryClient,
	privateShowId: string | null,
	data: TGetToysSessionResponse,
) => {
	if (useToysStore.getState().activePrivateShowId !== privateShowId) {
		return;
	}

	queryClient.setQueryData<TGetToysSessionResponse>([QUERY_KEYS.TOYS_SESSION, privateShowId], (current) =>
		current?.session_id === data.session_id ? keepLatestToysSession(current, data) : current,
	);
};

export const useToysSessionQuery = (sessionToken: string, privateShowId: string | null) => {
	const queryClient = useQueryClient();
	const {
		sessionId: storedSessionId,
		setProviderName,
		setIsDeviceOnline,
		setSessionState,
		setIsToyContainerDisabled,
	} = useToysStore();
	const processToysGateRequest = useToysGateRequest(sessionToken);

	const query = useAuthQuery({
		queryKey: [QUERY_KEYS.TOYS_SESSION, privateShowId],
		structuralSharing: (current, next) =>
			keepLatestToysSession(current as TGetToysSessionResponse | undefined, next as TGetToysSessionResponse),
		queryFn: sessionToken
			? async ({ signal }) => {
					const data = await processToysGateRequest({
						prefix: API_PREFIXES.GET_TOYS_SESSION,
						request: (token) => toysGateAPI.getToysSessionAsync({ token, signal }),
					});

					return data;
				}
			: skipToken,
		staleTime: STALE_INFINITY,
		gcTime: STALE_INFINITY,
		retry: false,
		retryOnMount: false,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

	useEffect(() => {
		if (!sessionToken || !query.isSuccess || !query.data?.session_id) {
			return;
		}

		// Both the container and popup observe this query; only refresh once per session change.
		const { activePrivateShowId, sessionId, setSessionId } = useToysStore.getState();

		if (privateShowId !== activePrivateShowId || sessionId === query.data.session_id) {
			return;
		}

		setSessionId(query.data.session_id);

		const queryKey = [QUERY_KEYS.TOYS_CAPABILITIES, privateShowId];

		if (sessionId) {
			queryClient.cancelQueries({ queryKey });
		}

		queryClient.invalidateQueries({ queryKey });
	}, [privateShowId, query.data?.session_id, query.isSuccess, queryClient, sessionToken, storedSessionId]);

	useEffect(() => {
		if (!query.isSuccess) {
			return;
		}

		setIsToyContainerDisabled(query.data?.state !== EToySessionState.GRANTED_RUNNING);
		setSessionState(query.data?.state ?? EToySessionState.IDLE);
		setIsDeviceOnline(query.data?.device.online ?? false);
		setProviderName((capitalizeFirstLetter(query.data?.device.provider ?? "") as EProviderName) ?? "");
	}, [
		query.data?.device.online,
		query.data?.device.provider,
		query.data?.state,
		query.isSuccess,
		setIsDeviceOnline,
		setProviderName,
		setSessionState,
		setIsToyContainerDisabled,
	]);

	return query;
};

export const useToysCapabilitiesQuery = (sessionToken: string, privateShowId: string | null) => {
	const processToysGateRequest = useToysGateRequest(sessionToken);

	return useAuthQuery({
		queryKey: [QUERY_KEYS.TOYS_CAPABILITIES, privateShowId],
		queryFn: sessionToken
			? async ({ signal }) => {
					const data = await processToysGateRequest({
						prefix: API_PREFIXES.GET_TOYS_CAPABILITIES,
						request: (token) => toysGateAPI.getCapabilitiesAsync({ token, signal }),
					});

					return data;
				}
			: skipToken,
		staleTime: STALE_INFINITY,
		gcTime: STALE_INFINITY,
		retry: false,
		retryOnMount: false,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
};

export const useUpdateToysMotionMutation = (sessionToken: string) => {
	const queryClient = useQueryClient();
	const processToysGateRequest = useToysGateRequest(sessionToken);
	const { activePrivateShowId } = useToysStore();

	return useMutation({
		mutationKey: [QUERY_KEYS.TOYS_SESSION, activePrivateShowId, API_PREFIXES.UPDATE_TOYS_MOTION],
		retry: false,
		mutationFn: async (data: Omit<IUpdateMotionRequest, "token">) =>
			processToysGateRequest({
				prefix: API_PREFIXES.UPDATE_TOYS_MOTION,
				request: (token) => toysGateAPI.updateMotionAsync({ token, motion: data.motion, preset_id: data.preset_id }),
			}),
		onSuccess: (data) => {
			updateToysSessionCache(queryClient, activePrivateShowId, data);
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TOYS_SESSION, activePrivateShowId] });
		},
	});
};

export const useAcceptToysControlMutation = (sessionToken: string) => {
	const processToysGateRequest = useToysGateRequest(sessionToken);

	return useMutation({
		retry: false,
		mutationFn: async () =>
			processToysGateRequest({
				prefix: API_PREFIXES.ACCEPT_TOYS_CONTROL,
				request: (token) => toysGateAPI.acceptAsync({ token }),
			}),
	});
};

export const usePlayToysMutation = (sessionToken: string) => {
	const queryClient = useQueryClient();
	const processToysGateRequest = useToysGateRequest(sessionToken);
	const { activePrivateShowId } = useToysStore();

	return useMutation({
		mutationKey: [QUERY_KEYS.TOYS_SESSION, activePrivateShowId, API_PREFIXES.PLAY_TOYS],
		retry: false,
		mutationFn: async (data: Omit<IPlayRequest, "token">) =>
			processToysGateRequest({
				prefix: API_PREFIXES.PLAY_TOYS,
				request: (token) => toysGateAPI.playAsync({ token, motion: data.motion, preset_id: data.preset_id }),
			}),
		onSuccess: (data) => updateToysSessionCache(queryClient, activePrivateShowId, data),
	});
};

export const useStopToysMutation = (sessionToken: string) => {
	const queryClient = useQueryClient();
	const processToysGateRequest = useToysGateRequest(sessionToken);
	const { activePrivateShowId } = useToysStore();

	return useMutation({
		mutationKey: [QUERY_KEYS.TOYS_SESSION, activePrivateShowId, API_PREFIXES.STOP_TOYS],
		retry: false,
		mutationFn: async () =>
			processToysGateRequest({
				prefix: API_PREFIXES.STOP_TOYS,
				request: (token) => toysGateAPI.stopAsync({ token }),
			}),
		onSuccess: (data) => updateToysSessionCache(queryClient, activePrivateShowId, data),
	});
};

export const useDeclineToysControlMutation = (sessionToken: string) => {
	const processToysGateRequest = useToysGateRequest(sessionToken);

	return useMutation({
		retry: false,
		mutationFn: async () =>
			processToysGateRequest({
				prefix: API_PREFIXES.DECLINE_TOYS_CONTROL,
				request: (token) => toysGateAPI.declineAsync({ token }),
			}),
	});
};
